import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { 
  X, 
  Trash2, 
  ShoppingBag, 
  Tag, 
  ArrowRight, 
  ShieldCheck, 
  AlertCircle,
  Plus,
  Minus
} from 'lucide-react';
import { useLojaCart } from '../../context/LojaCartContext';

export default function CartDrawer() {
  const navigate = useNavigate();
  const { 
    cartItems, 
    isCartOpen, 
    closeCart, 
    updateQuantity, 
    removeFromCart, 
    subtotal, 
    discount, 
    total, 
    totalItemsCount,
    appliedCoupon,
    applyCoupon,
    removeCoupon,
    couponError
  } = useLojaCart();

  const [couponInput, setCouponInput] = useState('');
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  if (!isCartOpen) return null;

  const handleApplyCoupon = async (e) => {
    e.preventDefault();
    if (!couponInput.trim()) return;
    setIsApplyingCoupon(true);
    await applyCoupon(couponInput);
    setIsApplyingCoupon(false);
  };

  const handleProceedToCheckout = () => {
    closeCart();
    navigate('/loja/checkout');
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
        onClick={closeCart}
      />

      {/* Drawer Container */}
      <div 
        className="relative w-full max-w-md bg-urb-dark h-full border-l border-urb-border shadow-2xl flex flex-col z-10 animate-in slide-in-from-right duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Cabeçalho */}
        <div className="flex items-center justify-between p-5 border-b border-urb-border">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-urb-primary/10 border border-urb-primary/30 flex items-center justify-center text-urb-primary">
              <ShoppingBag className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-base font-extrabold text-white tracking-wide uppercase">
                Meu Carrinho
              </h3>
              <span className="text-xs text-urb-text-muted">
                {totalItemsCount} {totalItemsCount === 1 ? 'item adicionado' : 'itens adicionados'}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={closeCart}
            className="w-9 h-9 rounded-full bg-urb-card-light hover:bg-white/10 text-urb-text-muted hover:text-white flex items-center justify-center transition-colors cursor-pointer"
            aria-label="Fechar carrinho"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Lista de Itens ou Estado Vazio */}
        {cartItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-4">
            <div className="w-20 h-20 rounded-full bg-urb-card flex items-center justify-center border border-urb-border text-urb-text-muted">
              <ShoppingBag className="w-8 h-8 opacity-40" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-white">Seu carrinho está vazio</h4>
              <p className="text-xs text-urb-text-secondary mt-1 max-w-xs">
                Explore a coleção de performance da Loja URB e vista a comunidade nas suas próximas provas.
              </p>
            </div>
            <button
              type="button"
              onClick={closeCart}
              className="px-6 py-3 rounded-xl bg-urb-primary text-black font-extrabold text-xs uppercase tracking-wider hover:bg-urb-primary-hover transition-all cursor-pointer"
            >
              EXPLORAR PRODUTOS
            </button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5 space-y-4 no-scrollbar">
            {cartItems.map((item) => (
              <div 
                key={item.itemKey}
                className="flex gap-3.5 p-3.5 rounded-2xl bg-urb-card border border-urb-border/70 hover:border-urb-border transition-colors"
              >
                {/* Imagem do Produto */}
                <div className="w-20 h-20 rounded-xl bg-urb-card-light/50 p-2 flex items-center justify-center border border-urb-border/50 shrink-0">
                  <img
                    src={item.image}
                    alt={item.productName}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      if (!e.target.src.includes('/loja/')) {
                        e.target.src = `/loja${item.image}`;
                      }
                    }}
                  />
                </div>

                {/* Detalhes do Item */}
                <div className="flex-1 flex flex-col justify-between overflow-hidden">
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="text-xs sm:text-sm font-bold text-white truncate leading-snug">
                        {item.productName}
                      </h4>
                      <button
                        type="button"
                        onClick={() => removeFromCart(item.itemKey)}
                        className="text-urb-text-muted hover:text-red-400 transition-colors p-1 -mr-1"
                        title="Remover item"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <div className="flex items-center gap-2 text-[11px] text-urb-text-secondary mt-0.5">
                      {item.size && (
                        <span>Tam: <strong className="text-white">{item.size}</strong></span>
                      )}
                      {item.color && (
                        <span>• Cor: <strong className="text-white">{item.color}</strong></span>
                      )}
                    </div>
                  </div>

                  {/* Preço e Ajuste de Quantidade */}
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-urb-border/40">
                    <span className="text-sm font-extrabold text-white">
                      R$ {(item.price * item.quantity).toFixed(2).replace('.', ',')}
                    </span>

                    <div className="flex items-center bg-urb-dark rounded-lg border border-urb-border p-0.5">
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.itemKey, -1)}
                        className="w-6 h-6 rounded flex items-center justify-center text-urb-text-secondary hover:text-white hover:bg-white/10"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-6 text-center text-xs font-bold text-white">
                        {item.quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.itemKey, 1)}
                        disabled={item.quantity >= item.maxStock}
                        className="w-6 h-6 rounded flex items-center justify-center text-urb-text-secondary hover:text-white hover:bg-white/10 disabled:opacity-30"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                </div>

              </div>
            ))}
          </div>
        )}

        {/* Rodapé: Cupom, Totais e CTA (Apenas se tiver itens) */}
        {cartItems.length > 0 && (
          <div className="p-5 border-t border-urb-border bg-urb-dark/95 space-y-4">
            
            {/* Campo de Cupom */}
            <div>
              {appliedCoupon ? (
                <div className="flex items-center justify-between p-3 rounded-xl bg-urb-primary/10 border border-urb-primary/30">
                  <div className="flex items-center gap-2 text-xs font-bold text-urb-primary">
                    <Tag className="w-3.5 h-3.5" />
                    <span>CUPOM: <strong>{appliedCoupon.code}</strong> (-R$ {discount.toFixed(2).replace('.', ',')})</span>
                  </div>
                  <button
                    type="button"
                    onClick={removeCoupon}
                    className="text-urb-text-muted hover:text-red-400 p-1"
                    title="Remover cupom"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <form onSubmit={handleApplyCoupon} className="flex gap-2">
                  <div className="relative flex-1">
                    <Tag className="w-3.5 h-3.5 text-urb-text-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value)}
                      placeholder="Cupom (ex: URB10)"
                      className="w-full pl-9 pr-3 py-2 rounded-xl bg-urb-card border border-urb-border text-xs text-white uppercase placeholder:normal-case placeholder-urb-text-muted focus:outline-none focus:border-urb-primary"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isApplyingCoupon || !couponInput.trim()}
                    className="px-4 py-2 rounded-xl bg-urb-card-light hover:bg-urb-primary hover:text-black text-white text-xs font-bold uppercase transition-all disabled:opacity-40 cursor-pointer border border-urb-border"
                  >
                    Aplicar
                  </button>
                </form>
              )}

              {couponError && (
                <div className="flex items-center gap-1.5 text-[11px] text-red-400 mt-1.5">
                  <AlertCircle className="w-3 h-3" />
                  <span>{couponError}</span>
                </div>
              )}
            </div>

            {/* Resumo Financeiro */}
            <div className="space-y-1.5 text-xs text-urb-text-secondary pt-1">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span className="text-white font-medium">R$ {subtotal.toFixed(2).replace('.', ',')}</span>
              </div>
              
              {discount > 0 && (
                <div className="flex justify-between text-urb-primary">
                  <span>Desconto de Cupom</span>
                  <span className="font-bold">- R$ {discount.toFixed(2).replace('.', ',')}</span>
                </div>
              )}

              <div className="flex justify-between text-[11px] text-urb-text-muted">
                <span>Frete</span>
                <span>Calculado na próxima etapa</span>
              </div>

              <div className="flex justify-between items-baseline pt-2 border-t border-urb-border/60 text-sm font-black text-white">
                <span className="uppercase tracking-wider">Total Estimado</span>
                <span className="text-xl text-urb-primary">
                  R$ {total.toFixed(2).replace('.', ',')}
                </span>
              </div>
            </div>

            {/* Botão Finalizar Compra */}
            <button
              type="button"
              onClick={handleProceedToCheckout}
              className="w-full py-4 rounded-xl bg-urb-primary text-black font-black text-xs sm:text-sm uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-urb-primary-hover shadow-[0_0_20px_rgba(138,230,0,0.3)] transition-all cursor-pointer"
            >
              <span>FECHAR PEDIDO</span>
              <ArrowRight className="w-4 h-4 stroke-[2.5]" />
            </button>

            {/* Aviso Seguro */}
            <div className="flex items-center justify-center gap-1.5 text-[11px] text-urb-text-muted">
              <ShieldCheck className="w-3.5 h-3.5 text-urb-primary" />
              <span>Checkout criptografado 100% seguro</span>
            </div>

          </div>
        )}

      </div>

    </div>
  );
}
